import * as React from 'react';
import classnames from 'classnames';
import { Fabric, TextField, CommandBarButton, IconButton } from '@fluentui/react';
import { List } from '@fluentui/react/lib/List';
import useDebouncedState from 'hooks/useDebouncedState';
import { ITheme, mergeStyleSets, getTheme, getFocusStyle } from '@fluentui/react/lib/Styling';
import { ValueType } from 'react-select';
import { useHistory } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { SelectOption } from 'formComponents/Select/types';
import { Select } from 'formComponents/Select/Select';
import { MultiSelect } from 'formComponents/Select/MultiSelect';
import { useKundeList, Kunde, KundeFilter } from 'api/kunde';
import { betreuerFilterItems, useBetreuer, useBetreuerByNr } from 'api/betreuer';
import { useBranchenAsOptions } from 'api/branche';
import { useErweiterteSuchenAsOptions } from 'api/erweiterteSuche';
import { useInteresseFuerProdukteAsOptions } from 'api/interesseFuerProdukte';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { KundeArt } from 'formComponents/lookups/KundeArt';
import * as XLSX from 'xlsx';
import { Maschinentype } from '../../formComponents/lookups/Maschinentype';
import { KundeSelect } from './KundeSelect';

const defaultMaxRows = 500;

const theme: ITheme = getTheme();
const { palette, semanticColors, fonts } = theme;

const styles = mergeStyleSets({
    container: {
        margin: 0,
        padding: '0 10px 10px 0',
        display: 'grid',
        gridTemplateRows: 'repeat(12, max-content) 1fr',
        paddingRight: 10,
        borderRight: '1px solid rgb(96, 94, 92)',
    },
    itemCell: [
        getFocusStyle(theme, { inset: -1 }),
        {
            minHeight: 54,
            padding: '5px 0',
            boxSizing: 'border-box',
            borderBottom: `1px solid ${semanticColors.bodyDivider}`,
            display: 'flex',
            width: 290,
            selectors: {
                '&:hover': { background: palette.neutralLight },
            },
        },
    ],
    clickable: {
        cursor: 'pointer',
    },
    active: {
        backgroundColor: '#CCC',
    },
    itemContent: {
        marginLeft: 10,
        overflow: 'hidden',
        flexGrow: 1,
    },
    itemName: [
        fonts.large,
        {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
    ],
    itemIndex: {
        fontSize: fonts.small.fontSize,
        color: palette.neutralTertiary,
        marginBottom: 10,
    },
    chevron: {
        alignSelf: 'center',
        marginLeft: 10,
        color: palette.neutralTertiary,
        fontSize: fonts.large.fontSize,
        flexShrink: 0,
    },
    searchLine: {
        marginBottom: 10,
    },
    twoCellLine: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridColumnGap: 10,
    },
    nrArtLine: {
        display: 'grid',
        gridTemplateColumns: '120px 1fr',
        gridColumnGap: 10,
    },
    searchTextLine: {
        marginBottom: 10,
    },
    searchTextFromBeginning: {
        marginTop: 0,
    },
    searchTextMultiPart: {
        marginTop: 0,
    },
    infoLine: {
        display: 'flex',
    },
    reset: {
        marginLeft: 'auto',
    },
});

interface KundeSearchProps {
    className?: string;
    kundeNr: number | null;
    onChangeKundeFilter?: (kundeFilter: KundeFilter) => void;
    allowOpenDetails?: boolean;
    onSelectKunde?: (kunde: Kunde) => void;
    // openDetails?: () => void;
}

interface FilterValues {
    maxRows: number;
    nr: number | null;
    art: string;
    searchText: string;
    searchTextFromBeginning: boolean;
    searchTextMultiPart: boolean;
    konzernNr: number | null;
    konzernName: string | null;
    betreuerFilter: SelectOption;
    telBetreuerFilter: SelectOption;
    plzVon: string;
    plzBis: string;
    umsatzTsdVon: number | null;
    umsatzTsdBis: number | null;
    maschine: string;
    mtNr: number;
    branchenOptions: SelectOption[];
    // produktgruppenOptions: SelectOption[];
    interesseFuerProdukteOptions: SelectOption[];
    erweiterteSuchenOptions: SelectOption[];
}

const defaultFilterValues: FilterValues = {
    maxRows: defaultMaxRows,
    nr: null,
    art: '__all__',
    searchText: '',
    searchTextFromBeginning: false,
    searchTextMultiPart: true,
    konzernNr: null,
    konzernName: null,
    betreuerFilter: betreuerFilterItems[0],
    telBetreuerFilter: betreuerFilterItems[0],
    plzVon: '',
    plzBis: '',
    umsatzTsdVon: null,
    umsatzTsdBis: null,
    maschine: '',
    mtNr: 0,
    branchenOptions: [],
    // produktgruppenOptions: [],
    erweiterteSuchenOptions: [],
    interesseFuerProdukteOptions: [],
};

export const KundeSearch: React.FC<KundeSearchProps> = ({
    className,
    onChangeKundeFilter,
    allowOpenDetails = false,
    onSelectKunde,
}) => {
    const auth = useSelector(authSelector);
    const history = useHistory();
    const betreuer = useBetreuer();
    const betreuerByNr = useBetreuerByNr();
    const branchenOptions = useBranchenAsOptions(true);
    // const produktgruppenOptions = useProduktgruppenAsOptions();
    const interesseFuerProdukteOptions = useInteresseFuerProdukteAsOptions(true);
    const erweiterteSuchenOptions = useErweiterteSuchenAsOptions(true);

    const betreuerFilterOptions = React.useMemo(() => {
        if (auth.rolle === 'AD') {
            return betreuerFilterItems;
        }

        return [
            ...betreuerFilterItems,
            ...betreuer
                .filter((b) => b.aktiv && b.name) // && (b.rolle === 'GF' || b.rolle === 'AD'))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map<SelectOption>((b) => ({ value: b.nr.toString(), label: b.name })),
        ];
    }, [auth, betreuer]);

    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>(defaultFilterValues);

    const filter = React.useMemo<KundeFilter>(
        () => ({
            nrOrDebNrOrKredNr: debouncedFilterValues.nr,
            art: debouncedFilterValues.art === '__all__' ? null : debouncedFilterValues.art,
            searchText: debouncedFilterValues.searchText,
            searchTextFromBeginning: debouncedFilterValues.searchTextFromBeginning,
            searchTextMultiPart: debouncedFilterValues.searchTextMultiPart,
            konzernNr: debouncedFilterValues.konzernNr,
            betreuer: parseInt(debouncedFilterValues.betreuerFilter.value, 10),
            telBetreuer: parseInt(debouncedFilterValues.telBetreuerFilter.value, 10),
            plzVon: debouncedFilterValues.plzVon,
            plzBis: debouncedFilterValues.plzBis || debouncedFilterValues.plzVon,
            umsatzVon: debouncedFilterValues.umsatzTsdVon === null ? null : debouncedFilterValues.umsatzTsdVon * 1000,
            umsatzBis: debouncedFilterValues.umsatzTsdBis === null ? null : debouncedFilterValues.umsatzTsdBis * 1000,
            maschine: debouncedFilterValues.maschine,
            mtNr: debouncedFilterValues.mtNr,
            branchen: debouncedFilterValues.branchenOptions.map((option) => parseInt(option.value, 10)),
            // produktgruppen: debouncedFilterValues.produktgruppenOptions.map((option) => parseInt(option.value, 10)),
            interesseFuerProdukte: debouncedFilterValues.interesseFuerProdukteOptions.map((option) =>
                parseInt(option.value, 10),
            ),
            erweiterteSuchen: debouncedFilterValues.erweiterteSuchenOptions.map((option) => parseInt(option.value, 10)),
            maxRows: debouncedFilterValues.maxRows,
        }),
        [debouncedFilterValues],
    );

    React.useEffect(() => {
        if (onChangeKundeFilter) {
            onChangeKundeFilter(filter);
        }
    }, [filter, onChangeKundeFilter]);

    const kundeList = useKundeList(filter);

    const downloadXlsx = () => {
        const data: string[][] = [['Nr', 'Name 1', 'Name2', 'Betreuer', 'PLZ', 'Ort']];

        (kundeList.data || []).forEach((kunde) => {
            data.push([
                kunde.nr.toString(),
                kunde.name1,
                kunde.name2,
                kunde.betreuer
                    ? betreuerByNr[kunde.betreuer]?.name ||
                      betreuerByNr[kunde.betreuer]?.kz ||
                      kunde.betreuer.toString()
                    : '',
                kunde.plz,
                kunde.ort,
            ]);
        });

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet(data);

        XLSX.utils.book_append_sheet(workbook, worksheet, 'Kundenliste');

        const content = XLSX.write(workbook, {
            bookType: 'xlsx',
            type: 'buffer',
            compression: true,
            bookSST: true,
        });

        const file = new Blob([content], {
            type: 'application/octet-stream',
        });

        const element = document.createElement('a');
        element.href = URL.createObjectURL(file);
        element.download = 'Kundenliste.xlsx';
        document.body.appendChild(element);
        element.click();
    };

    const reset = () => {
        setFilterValues(defaultFilterValues);
    };

    const onRenderCell = (kunde?: Kunde): JSX.Element => {
        return (
            <div
                className={classnames(styles.itemCell, { [styles.clickable]: allowOpenDetails })}
                data-is-focusable
                onClick={() => {
                    if (kunde) {
                        if (onSelectKunde) {
                            onSelectKunde(kunde);
                        } else if (allowOpenDetails) {
                            history.push('/kunden/' + kunde.nr);
                            // openDetails();
                        }
                    }
                }}
            >
                <div className={styles.itemContent}>
                    <div className={styles.itemName}>
                        {kunde?.name1} {kunde?.name2}
                    </div>
                    <div className={styles.itemIndex}>
                        {kunde?.betreuer
                            ? betreuerByNr[kunde?.betreuer]?.name ||
                              betreuerByNr[kunde?.betreuer]?.kz ||
                              kunde?.betreuer
                            : ''}
                        {' / '} {kunde?.nr}
                    </div>
                    <div>
                        {kunde?.plz} {kunde?.ort}
                    </div>
                </div>
            </div>
        );
    };

    const count = (kundeList.data || []).length;

    return (
        <Fabric className={classnames(styles.container, className)}>
            <div className={styles.searchTextLine}>
                <TextField
                    value={filterValues.searchText}
                    onChange={(event, newValue): void => {
                        setFilterValues({
                            ...filterValues,
                            searchText: newValue === undefined ? '' : newValue,
                        });
                    }}
                    placeholder="Name, Ort, Suchfeld"
                />
            </div>
            <div className={classnames(styles.searchLine, styles.twoCellLine)}>
                <Checkbox
                    className={styles.searchTextFromBeginning}
                    label="Anfang"
                    checked={filterValues.searchTextFromBeginning}
                    inputProps={{
                        style: {
                            marginTop: -5,
                            fontSize: 10,
                        },
                    }}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            searchTextFromBeginning: checked || false,
                        });
                    }}
                />
                <Checkbox
                    className={styles.searchTextMultiPart}
                    label="Jedes Word extra"
                    checked={filterValues.searchTextMultiPart}
                    inputProps={{
                        style: {
                            marginTop: -5,
                            fontSize: 10,
                        },
                    }}
                    onChange={(event, checked) => {
                        setFilterValues({
                            ...filterValues,
                            searchTextMultiPart: checked || false,
                        });
                    }}
                />
            </div>
            <div className={classnames(styles.searchLine, styles.nrArtLine)}>
                <TextField
                    type="number"
                    value={filterValues.nr === null ? '' : filterValues.nr.toString()}
                    onChange={(event, newValue): void => {
                        setFilterValues({
                            ...filterValues,
                            nr: newValue === '' || newValue === undefined ? null : parseInt(newValue, 10),
                        });
                    }}
                    placeholder="Kundennr."
                />
                <KundeArt
                    value={filterValues.art}
                    onChange={(newValue): void => {
                        setFilterValues({
                            ...filterValues,
                            art: newValue,
                        });
                    }}
                    addAll
                    renderLabel="placeholder"
                />
            </div>

            <div className={styles.searchTextLine}>
                <KundeSelect
                    placeholder="Konzern"
                    kNr={filterValues?.konzernNr ?? undefined}
                    name={filterValues?.konzernName ?? ''}
                    allowEmpty
                    onChange={(newKNr, newKunde): void => {
                        setFilterValues({
                            ...filterValues,
                            konzernNr: newKNr ?? null,
                            konzernName: newKunde?.name1 ?? null,
                        });
                    }}
                />
            </div>

            <div className={classnames(styles.searchLine, styles.twoCellLine)}>
                <Select
                    name="betreuer"
                    value={filterValues.betreuerFilter}
                    options={betreuerFilterOptions}
                    onChange={(value: ValueType<SelectOption, false>): void => {
                        setFilterValues({
                            ...filterValues,
                            betreuerFilter: value as SelectOption,
                        });
                    }}
                />
                <Select
                    name="telBetreuer"
                    value={filterValues.telBetreuerFilter}
                    options={betreuerFilterOptions}
                    onChange={(value: ValueType<SelectOption, false>): void => {
                        setFilterValues({
                            ...filterValues,
                            telBetreuerFilter: value as SelectOption,
                        });
                    }}
                />
            </div>
            <div className={classnames(styles.searchLine, styles.twoCellLine)}>
                <TextField
                    placeholder="PLZ (von)"
                    value={filterValues.plzVon}
                    onChange={(event, newValue) => {
                        setFilterValues({
                            ...filterValues,
                            plzVon: newValue || '',
                        });
                    }}
                />
                <TextField
                    placeholder="PLZ (bis)"
                    value={filterValues.plzBis}
                    onChange={(event, newValue) => {
                        setFilterValues({
                            ...filterValues,
                            plzBis: newValue || '',
                        });
                    }}
                />
            </div>
            <div className={classnames(styles.searchLine, styles.twoCellLine)}>
                <TextField
                    placeholder="Umsatz (von TSD)"
                    type="number"
                    value={filterValues.umsatzTsdVon === null ? '' : filterValues.umsatzTsdVon.toString()}
                    onChange={(event, newValue) => {
                        setFilterValues({
                            ...filterValues,
                            umsatzTsdVon: (newValue || '').trim() === '' ? null : parseInt(newValue || '', 10),
                        });
                    }}
                />
                <TextField
                    placeholder="Umsatz (bis TSD)"
                    type="number"
                    value={filterValues.umsatzTsdBis === null ? '' : filterValues.umsatzTsdBis.toString()}
                    onChange={(event, newValue) => {
                        setFilterValues({
                            ...filterValues,
                            umsatzTsdBis: (newValue || '').trim() === '' ? null : parseInt(newValue || '', 10),
                        });
                    }}
                />
            </div>
            <div className={styles.searchLine}>
                <MultiSelect
                    name="filterBranche"
                    value={filterValues.branchenOptions}
                    placeholder="Branchen..."
                    options={branchenOptions}
                    onChange={(values: ValueType<SelectOption, true>): void => {
                        setFilterValues({
                            ...filterValues,
                            branchenOptions: (values || []) as SelectOption[],
                        });
                    }}
                />
            </div>
            {/*
            <div className={styles.searchLine}>
                <MultiSelect
                    name="filterProduktgruppe"
                    value={filterValues.produktgruppenOptions}
                    placeholder="Produktgruppen..."
                    options={produktgruppenOptions}
                    onChange={(values: ValueType<SelectOption, true>): void => {
                        setFilterValues({
                            ...filterValues,
                            produktgruppenOptions: (values || []) as SelectOption[],
                        });
                    }}
                />
            </div>
            */}
            <div className={styles.searchLine}>
                <MultiSelect
                    name="filterInteresseFuerProdukte"
                    value={filterValues.interesseFuerProdukteOptions}
                    placeholder="Interesse für Produkte..."
                    options={interesseFuerProdukteOptions}
                    onChange={(values: ValueType<SelectOption, true>): void => {
                        setFilterValues({
                            ...filterValues,
                            interesseFuerProdukteOptions: (values || []) as SelectOption[],
                        });
                    }}
                />
            </div>
            <div className={styles.searchLine}>
                <MultiSelect
                    name="filterErweiterteSuche"
                    value={filterValues.erweiterteSuchenOptions}
                    placeholder="Erweiterte Suche..."
                    options={erweiterteSuchenOptions}
                    onChange={(values: ValueType<SelectOption, true>): void => {
                        setFilterValues({
                            ...filterValues,
                            erweiterteSuchenOptions: (values || []) as SelectOption[],
                        });
                    }}
                />
            </div>
            <div className={classnames(styles.searchLine, styles.twoCellLine)}>
                <TextField
                    value={filterValues.maschine}
                    onChange={(event, newValue): void => {
                        setFilterValues({
                            ...filterValues,
                            maschine: newValue === undefined ? '' : newValue,
                        });
                    }}
                    placeholder="Maschine"
                />
                <Maschinentype
                    disabled={false}
                    allowEmpty
                    placeholder="Alle Typen"
                    value={filterValues.mtNr}
                    onChange={(newMtNr) => {
                        setFilterValues({
                            ...filterValues,
                            mtNr: newMtNr,
                        });
                    }}
                />
            </div>
            <div className={classnames(styles.searchLine, styles.infoLine)}>
                <div>
                    <CommandBarButton
                        text={'Ergebnisse: ' + (count === filterValues.maxRows ? '>' + count : count)}
                        onClick={() => {
                            setFilterValues({
                                ...filterValues,
                                maxRows: filterValues.maxRows === 500 ? 100000 : 500,
                            });
                        }}
                    />
                </div>
                {(auth.rolle === 'GF' || auth.rolle === 'ID' || auth.rolle === 'EX') && (
                    <IconButton
                        onClick={() => {
                            downloadXlsx();
                        }}
                        iconProps={{ iconName: 'Download', style: { fontSize: 14 } }}
                        style={{ position: 'relative', marginLeft: -2, top: 2, width: 18, height: 18 }}
                    />
                )}
                <div className={styles.reset}>
                    <CommandBarButton text="Zurücksetzen" onClick={reset} />
                </div>
            </div>
            <div>
                <AutoSizer>
                    {({ height, width }) => (
                        <div style={{ width, height, overflowX: 'hidden', overflowY: 'scroll' }}>
                            <List items={kundeList.data || []} onRenderCell={onRenderCell} />
                        </div>
                    )}
                </AutoSizer>
            </div>
        </Fabric>
    );
};
